import React, { useState, useEffect } from 'react';
import { fetchLateDelivery } from '../services/api';
import DateRangeFilter from '../components/DateRangeFilter';
import { toTitleCase } from '../utils/format';

const DASH = String.fromCharCode(8212);
const ARROW_L = String.fromCharCode(8592);
const ARROW_R = String.fromCharCode(8594);
const todayIso = new Date().toISOString().split('T')[0];
const PAGE_SIZE = 50;
const fmt = n => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const FILTER_TYPES = [
  { value: '', label: 'All' },
  { value: 'Refund', label: 'Refunds' },
  { value: 'Reship', label: 'Reships' },
  { value: 'None', label: 'No Action' },
];

const refundOf = r => {
  const cap = Number(r.refundAuthCaptureAmt || 0);
  return cap > 0 ? cap : Number(r.refundCreditAmt || 0);
};
const classify = r => {
  const isRefund = refundOf(r) > 0;
  const isReship = !!r.reshipOrderId;
  return { isRefund, isReship, isNone: !isRefund && !isReship };
};

export default function RefundsReships({ navigateTo }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(todayIso);
  const [filterType, setFilterType] = useState('');
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchLateDelivery({ StartDate: start, EndDate: end })
      .then(res => setRows(Array.isArray(res) ? res : res?.data ?? []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tagged = rows.map(r => ({ ...r, _c: classify(r), _refund: refundOf(r) }));
  const refundCount = tagged.filter(r => r._c.isRefund).length;
  const reshipCount = tagged.filter(r => r._c.isReship).length;
  const noneCount = tagged.filter(r => r._c.isNone).length;
  const totalRefundAmt = tagged.reduce((s, r) => s + r._refund, 0);
  const totalReshipAmt = tagged.reduce((s, r) => s + Number(r.reshipAmt || 0), 0);

  const filtered = tagged.filter(r =>
    filterType === 'Refund' ? r._c.isRefund
    : filterType === 'Reship' ? r._c.isReship
    : filterType === 'None' ? r._c.isNone
    : true
  );
  const total = filtered.length;
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const cards = [
    { label: 'Late Orders', value: rows.length.toLocaleString() },
    { label: 'Refunds', value: refundCount.toLocaleString(), sub: fmt(totalRefundAmt), highlight: true },
    { label: 'Reships', value: reshipCount.toLocaleString(), sub: fmt(totalReshipAmt) },
    { label: 'No Action', value: noneCount.toLocaleString() },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Refunds &amp; Reships</h2>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>Late deliveries and their resolution (refund / reship / none)</div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Filters</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <DateRangeFilter start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} />
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>TYPE</div>
            <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }} className="form-select" style={{ fontSize: 11 }}>
              {FILTER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <button onClick={() => { setPage(1); load(); }} className="btn btn-primary" style={{ fontSize: 11 }}>Search</button>
        </div>
      </div>

      {rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
          {cards.map(({ label, value, sub, highlight }) => (
            <div key={label} className="card" style={{ padding: '10px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: highlight ? 'var(--orange)' : 'var(--navy)' }}>{value}</div>
              {sub && <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)' }}>{sub}</div>}
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-header">Late Deliveries {total > 0 && `(${total.toLocaleString()})`}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && filtered.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No records found.</div>
        )}
        {filtered.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order ID</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Ship Type</th>
                    <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Promised</th>
                    <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Actual</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Action</th>
                    <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Refund</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Reship</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row, i) => {
                    const { isRefund, isReship, isNone } = row._c;
                    return (
                      <tr key={row.orderId ?? i} onClick={() => navigateTo?.('order-details', row.bcOrderId ?? row.orderId)} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: 'pointer' }}>
                        <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600 }}>{row.bcOrderId ?? row.orderId ?? DASH}</td>
                        <td style={{ padding: '8px 8px', color: 'var(--navy)' }}>{toTitleCase(row.billTo ?? DASH)}</td>
                        <td style={{ padding: '8px 8px', color: 'var(--gray-500)' }}>{row.shipType ?? DASH}</td>
                        <td style={{ padding: '8px 8px', textAlign: 'center', color: 'var(--gray-500)' }}>{row.deliveryDate ?? DASH}</td>
                        <td style={{ padding: '8px 8px', textAlign: 'center', color: 'var(--gray-500)' }}>{row.actualDeliveryDate ?? DASH}</td>
                        <td style={{ padding: '8px 8px', whiteSpace: 'nowrap' }}>
                          {isRefund && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: '#fef3f0', color: '#c0392b', marginRight: 4 }}>Refund</span>}
                          {isReship && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: '#e8f0fe', color: '#00395C' }}>Reship</span>}
                          {isNone && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: 'var(--gray-100)', color: 'var(--gray-500)' }}>None</span>}
                        </td>
                        <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600, color: isRefund ? 'var(--red)' : 'var(--gray-400)' }}>
                          {isRefund ? fmt(row._refund) : DASH}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: isReship ? 'var(--navy)' : 'var(--gray-400)' }}>
                          {isReship ? fmt(row.reshipAmt) : DASH}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {pageCount > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 12 }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn btn-navy" style={{ fontSize: 11 }}>{ARROW_L} Prev</button>
                <span style={{ fontSize: 11, alignSelf: 'center' }}>Page {page} of {pageCount}</span>
                <button onClick={() => setPage(p => Math.min(pageCount, p + 1))} disabled={page >= pageCount} className="btn btn-navy" style={{ fontSize: 11 }}>Next {ARROW_R}</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
